// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx (ProgressBar pattern)
// See: .claude/skills/rehaut-design/HANDOFF.md
'use client';

import { useEffect, useState } from 'react';

/**
 * Thin (3-4px) progress bar with a single-accent fill. Animates from
 * 0% to `pct` on mount via a setTimeout that defers the width update
 * by `delay` ms, then transitions over 900ms with the v2 spring ease.
 * Honors prefers-reduced-motion (snaps to final value).
 *
 * Use 4px height for the primary package row; 3px for per-zone bars
 * inside a bundle card. Color stays the single accent — no semantic
 * tinting per the brief's "one accent total" rule.
 */
export function ProgressBar({
  pct,
  delay = 0,
  height = 4,
  trackColor,
}: {
  pct: number;
  delay?: number;
  height?: number;
  trackColor?: string;
}) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setWidth(pct);
      return;
    }
    const handle = setTimeout(() => setWidth(pct), delay);
    return () => clearTimeout(handle);
  }, [pct, delay]);

  const clamped = Math.max(0, Math.min(100, pct));

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        width: '100%',
        height: `${height}px`,
        background: trackColor || 'var(--hairline)',
        borderRadius: `${Math.max(2, Math.round(height / 2))}px`,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${width}%`,
          height: '100%',
          background: 'var(--accent)',
          borderRadius: 'inherit',
          transition: 'width 900ms var(--ease-spring)',
        }}
      />
    </div>
  );
}
